import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "antd";
import { ArrowRight, Calculator as CalculatorIcon } from "lucide-react";
import Navbar from "Components/Navbar/Navbar";
import Footer from "Components/Footer/Footer";
import Reveal from "Components/marketing/Reveal";
import Annotate from "Components/marketing/Annotate";
import OffsetCard from "Components/marketing/OffsetCard";
import StreamList from "Components/marketing/StreamList";
import StatementSection from "Components/marketing/StatementSection";
import { PLATFORM_PROOF } from "commons/earnings.constants";
import { calculateEarnings, formatInrCompact, formatInr } from "services/earnings.service";
import { trackEvent, ANALYTICS_EVENTS } from "helpers/analytics.helpers";

/** The worked example from the deck, derived rather than typed. */
const EXAMPLE = { members: 3000, bookingRate: 40 };

/** How most communities run today — the mess we're naming out loud. */
const TODAY = [
  "A WhatsApp group.",
  "A Google form, sometimes.",
  "Someone collecting money on UPI.",
  "Someone forgetting to pay.",
  "People asking, “where is the run starting?”",
  "And one organiser quietly doing most of the work.",
];

/** What changes on ShoUp. */
const FIXED = [
  "A place for members to join.",
  "Events people can easily register for.",
  "Updates everyone actually sees.",
  "A simple way to track members.",
  "Payments handled automatically.",
  "Bookings, orders and attendance in one dashboard.",
];

/** The objection this kills: “am I handing over my community?” */
const CONTROL = [
  "Your culture.",
  "Your vibe.",
  "How you move.",
  "Where you meet.",
  "What your community stands for.",
];

/** Registration is genuinely short — say so, and show it. */
const STEPS = [
  { title: "Create your community", body: "Name, city, what you do. A couple of minutes." },
  { title: "Host your first event", body: "Ticket tiers, timings, venue. Live in minutes." },
  { title: "Invite your members", body: "They join, book and keep showing up." },
  { title: "From there, momentum builds", body: "And your community starts earning." },
];

function LandingPage() {
  const example = calculateEarnings({
    members: EXAMPLE.members,
    bookingRatePct: EXAMPLE.bookingRate,
  });

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.LANDING_VIEWED);
  }, []);

  const trackCta = (location) =>
    trackEvent(ANALYTICS_EVENTS.LANDING_CTA_CLICKED, { location });

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      <main className="flex-1">
        {/* ── 1. Hero ─────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-gradient-shoup">
          <div className="container mx-auto px-4 py-20 md:px-6 md:py-32">
            <div className="flex flex-wrap items-end justify-between gap-10">
              <Reveal>
                <h1 className="text-statement max-w-[11ch] text-5xl md:text-7xl lg:text-8xl">
                  <span className="text-[hsl(var(--brand-lavender))]">
                    Your community
                  </span>
                  <br />
                  <span className="text-foreground">already exists.</span>
                </h1>
              </Reveal>

              <Reveal delay={140}>
                <OffsetCard className="max-w-sm !border-[hsl(var(--brand-coral))] !bg-[hsl(var(--brand-coral))]">
                  <p className="font-display text-3xl font-bold leading-tight tracking-tight text-white md:text-4xl">
                    We simply give it a home.
                  </p>
                </OffsetCard>
              </Reveal>
            </div>

            <div className="mt-16 grid max-w-4xl gap-8 md:grid-cols-3">
              <Reveal>
                <p className="text-base leading-relaxed text-foreground/70 md:text-lg">
                  Payments handled automatically. Members, events and updates in{" "}
                  <Annotate>one place</Annotate>.
                </p>
              </Reveal>
              <Reveal delay={90}>
                <p className="text-base leading-relaxed text-foreground/70 md:text-lg">
                  Every time your members book a class or an event, your community{" "}
                  <Annotate color="coral">earns a share</Annotate>.
                </p>
              </Reveal>
              <Reveal delay={180}>
                <p className="text-base leading-relaxed text-foreground/70 md:text-lg">
                  You keep doing what you do best:{" "}
                  <span className="font-semibold text-foreground">
                    bringing people together
                  </span>
                  .
                </p>
              </Reveal>
            </div>

            <Reveal delay={280}>
              <div className="mt-12 flex flex-col gap-3 sm:flex-row">
                <Link to="/signup" onClick={() => trackCta("hero")}>
                  <Button type="primary" size="large" className="px-8">
                    Register your community
                  </Button>
                </Link>
                <Link to="/calculator#estimate" onClick={() => trackCta("hero_calculator")}>
                  <Button
                    size="large"
                    className="bg-background/60 px-8 backdrop-blur-sm"
                    icon={<CalculatorIcon size={15} />}
                  >
                    See what you&apos;d earn
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── 2. Recognition ──────────────────────────────────────── */}
        <StatementSection
          eyebrow="Let's be honest"
          title="About how most communities work today."
          accent="coral"
        >
          <div className="mt-12 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {TODAY.map((line, i) => (
              <Reveal key={line} delay={i * 60}>
                <p className="border-l-2 border-border/60 pl-4 text-base leading-relaxed text-muted-foreground">
                  {line}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <p className="mt-14 font-display text-2xl font-semibold leading-snug tracking-tight md:text-3xl">
              <span className="text-[hsl(var(--brand-lavender))]">
                Communities grow.
              </span>{" "}
              <span className="text-foreground">
                However, the system around them doesn&apos;t.
              </span>
            </p>
          </Reveal>
        </StatementSection>

        {/* ── 3. Relief ───────────────────────────────────────────── */}
        <StatementSection
          eyebrow="What changes"
          title="ShoUp fixes the mess."
          lede={
            <>
              Your community finally <Annotate variant="circle">gets organised</Annotate>.
            </>
          }
          accent="coral"
          className="border-t border-border/40 bg-secondary"
        >
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FIXED.map((line, i) => (
              <Reveal key={line} delay={i * 60}>
                <div className="h-full rounded-2xl border border-border/50 bg-card p-6">
                  <p className="text-base leading-relaxed text-foreground/85">
                    {line}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={220}>
            <p className="mt-14 max-w-[34ch] font-display text-2xl font-semibold leading-snug tracking-tight text-foreground md:text-3xl">
              You keep doing what you already do best:{" "}
              <span className="italic text-[hsl(var(--brand-lavender))]">
                bring people together.
              </span>
            </p>
          </Reveal>
        </StatementSection>

        {/* ── 4. Money ────────────────────────────────────────────── */}
        <StatementSection
          eyebrow="And yes — you will earn"
          title="Every time your members show up, your community earns."
          lede="Not from followers. Not from promotions. From people actually showing up."
          accent="coral"
          className="scroll-mt-24 border-t border-border/40"
          id="earnings"
        >
          <div className="mt-14 grid gap-12 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <StreamList />
              <Reveal delay={280}>
                <p className="mt-9 font-display text-xl font-semibold italic text-[hsl(var(--brand-lavender))] md:text-2xl">
                  Participation is the currency, not follower count.
                </p>
              </Reveal>
            </div>

            <div className="space-y-5">
              <Reveal>
                <div className="rounded-3xl border border-border/60 bg-card p-7">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    For example
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    A{" "}
                    <span className="font-semibold text-foreground">
                      3,000 member
                    </span>{" "}
                    community where{" "}
                    <span className="font-semibold text-foreground">40% book</span>{" "}
                    each month earns roughly
                  </p>
                  <p className="mt-4 flex flex-wrap items-baseline gap-3">
                    <span className="font-display text-5xl font-bold leading-none tabular-nums text-[hsl(var(--brand-coral))]">
                      {formatInrCompact(example.annual)}
                    </span>
                    <span className="text-base text-muted-foreground">a year</span>
                  </p>
                  <p className="mt-3 text-sm text-muted-foreground">
                    That&apos;s about {formatInr(example.monthly)} a month, on top
                    of whatever you already collect.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={120}>
                <Link
                  to="/calculator#estimate"
                  className="block"
                  onClick={() => trackCta("earnings_calculator")}
                >
                  <OffsetCard className="!bg-[hsl(var(--brand-coral))] transition-transform hover:-translate-y-0.5">
                    <p className="font-display text-3xl font-bold leading-tight tracking-tight text-white">
                      Run your own numbers.
                    </p>
                    <p className="mt-3 text-base text-white/90">
                      Two sliders, thirty seconds.
                    </p>
                    <p className="mt-4 inline-flex items-center gap-2 font-semibold text-white underline underline-offset-4">
                      Open the calculator <ArrowRight size={16} />
                    </p>
                  </OffsetCard>
                </Link>
              </Reveal>
            </div>
          </div>
        </StatementSection>

        {/* ── 5. “Are we too small?” ──────────────────────────────── */}
        <section className="border-t border-border/40 bg-secondary py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <Reveal>
                <h2 className="text-statement max-w-[13ch] text-4xl text-[hsl(var(--brand-coral))] md:text-5xl lg:text-6xl">
                  Small communities can win big here.
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <div className="space-y-6">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    A 50 person run club that shows up every week can earn more
                    than a 1,000 member WhatsApp group that doesn&apos;t.
                  </p>
                  <p className="font-display text-2xl font-semibold leading-snug tracking-tight md:text-3xl">
                    <span className="italic text-[hsl(var(--brand-lavender))]">
                      Energy
                    </span>{" "}
                    <span className="text-foreground">beats size.</span>
                    <br />
                    <span className="italic text-[hsl(var(--brand-lavender))]">
                      Consistency
                    </span>{" "}
                    <span className="text-foreground">beats hype.</span>
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ── 6. Control ──────────────────────────────────────────── */}
        <StatementSection
          eyebrow="You're not handing anything over"
          title="You still control everything that matters."
          accent="coral"
          className="border-t border-border/40"
        >
          <div className="mt-12 flex flex-wrap gap-3">
            {CONTROL.map((line, i) => (
              <Reveal key={line} delay={i * 60}>
                <span className="inline-block rounded-full border border-[hsl(var(--brand-lavender)/0.35)] bg-[hsl(var(--brand-lavender)/0.08)] px-5 py-2.5 text-base text-foreground/90">
                  {line}
                </span>
              </Reveal>
            ))}
          </div>

          <Reveal delay={220}>
            <p className="mt-12 font-display text-2xl font-semibold leading-snug tracking-tight text-foreground md:text-3xl">
              ShoUp simply{" "}
              <span className="text-[hsl(var(--brand-lavender))]">
                removes the friction
              </span>{" "}
              around it.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-14 rounded-3xl border border-border/50 bg-card p-8 md:p-10">
              <p className="font-display text-2xl font-bold leading-tight tracking-tight text-foreground md:text-3xl">
                Think of ShoUp like YouTube — but for communities.
              </p>
              <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground">
                You bring the people. ShoUp provides the platform. The more people
                participate, the more the community grows — and instead of messages
                getting lost in chats, your members register for events, track what
                they&apos;ve joined and actually get your updates.
              </p>
            </div>
          </Reveal>
        </StatementSection>

        {/* ── 7. Proof + getting started ──────────────────────────── */}
        <section className="border-t border-border/40 bg-secondary py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-6">
            <Reveal>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <span className="font-display text-5xl font-bold tabular-nums text-[hsl(var(--brand-lavender))] md:text-6xl">
                  {PLATFORM_PROOF.communities}
                </span>
                <span className="font-display text-2xl font-semibold text-foreground md:text-3xl">
                  communities,
                </span>
                <span className="font-display text-5xl font-bold tabular-nums text-[hsl(var(--brand-lavender))] md:text-6xl">
                  {PLATFORM_PROOF.membersLabel}
                </span>
                <span className="font-display text-2xl font-semibold text-foreground md:text-3xl">
                  members, and counting.
                </span>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h2 className="text-statement mt-16 max-w-[16ch] text-4xl text-[hsl(var(--brand-coral))] md:text-5xl">
                Getting started takes a few minutes.
              </h2>
            </Reveal>

            <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((step, i) => (
                <Reveal as="li" key={step.title} delay={i * 80}>
                  <div className="h-full rounded-2xl border border-border/50 bg-card p-6">
                    <span className="font-display text-2xl font-bold tabular-nums text-[hsl(var(--brand-lavender))]">
                      {i + 1}
                    </span>
                    <h3 className="mt-3 font-display text-lg font-semibold tracking-tight text-foreground">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {step.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>

            <Reveal delay={360}>
              <div className="mt-14 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to="/signup" onClick={() => trackCta("footer")}>
                  <Button type="primary" size="large" className="px-8">
                    Register your community
                  </Button>
                </Link>
                <Link to="/calculator#estimate" onClick={() => trackCta("footer_calculator")}>
                  <Button size="large" className="px-8">
                    See what you&apos;d earn
                  </Button>
                </Link>
                <p className="text-sm text-muted-foreground sm:ml-4">
                  Free to list on. No lock-ins. Questions?{" "}
                  <a
                    href="mailto:info@weshoup.com"
                    className="font-semibold text-primary hover:underline"
                  >
                    info@weshoup.com
                  </a>
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export { LandingPage as default };
